import { App as AntApp } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { shortTime } from "./timetable.ts";

export type LessonDetails = components["schemas"]["LessonDetails"];
export type LessonStatus = components["schemas"]["LessonStatus"];
export type CancellationCause = NonNullable<components["schemas"]["CancellationCause"]>;
export type LessonChangeDetails = components["schemas"]["LessonChangeDetails"];
export type LessonChangeKind = components["schemas"]["LessonChangeKind"];
export type LessonWarning = components["schemas"]["LessonWarning"];

/** Warnings stay until closed or for this many seconds: the change is already saved, so they must not flash past. */
const WARNING_SECONDS = 15;

export const cancellationCauses: CancellationCause[] = ["School", "Teacher"];

export const lessonKeys = {
  all: (classId: string) => ["academic", "lessons", classId] as const,
  week: (classId: string, from: string) => ["academic", "lessons", classId, from] as const,
  changes: (classId: string, lessonId: string) => ["academic", "lessons", classId, "changes", lessonId] as const,
};

/** Monday of the week containing the date (the school week starts on Monday, whatever the locale says). */
export function weekStart(date: Dayjs): Dayjs {
  return date.subtract((date.day() + 6) % 7, "day").startOf("day");
}

/** The week shown first: the current one when today lies inside the academic year, otherwise the year's first week. */
export function initialWeek(yearStart: string, yearEnd: string): Dayjs {
  const today = dayjs();
  const inside = !today.isBefore(dayjs(yearStart), "day") && !today.isAfter(dayjs(yearEnd), "day");
  return weekStart(inside ? today : dayjs(yearStart));
}

/**
 * Shows the warnings a saved move, substitute (set or removed) or restore came back with (bd-15q.34, bd-15q.37): for now only a teacher of record who is
 * already teaching another class at overlapping times. The change itself is not undone.
 */
export function useShowLessonWarnings(): (warnings: readonly LessonWarning[] | null | undefined) => void {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();
  return (warnings) => {
    for (const warning of warnings ?? []) {
      notification.warning({
        key: `${warning.kind}-${warning.otherLessonId}`,
        title: t(`lessons.warnings.${warning.kind}Title`),
        description: t(`lessons.warnings.${warning.kind}`, {
          teacher: warning.teacherName ?? t("lessons.noTeacher"),
          class: warning.className,
          subject: warning.subjectName,
          time: `${shortTime(warning.startTime)}–${shortTime(warning.endTime)}`,
        }),
        duration: WARNING_SECONDS,
      });
    }
  };
}
